import { onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { downloadFh2WaylineKmz } from '/@/api/fh2'
import {
  clearWaylinePreviewOnGMap,
  hasWaylinePreviewGeometry,
  renderWaylineModelOnGMap,
} from '/@/hooks/wayline-gmap-preview'
import { getRoot } from '/@/root'
import { useMyStore } from '/@/store'
import { ERouterName } from '/@/types'
import { parseWaylineKmzModel } from '/@/utils/parse-wayline-kmz'

const PREFIX = '__fh2_wayline_prev__'

/**
 * 司空「项目航线库」：根据 store 选中项在共享 GMap 上预览 KMZ 轨迹。
 */
export function useFh2WaylineMapPreview () {
  const store = useMyStore()
  const root = getRoot()
  const route = useRoute()
  let reqSeq = 0
  let retryTimer: ReturnType<typeof setTimeout> | undefined

  function clearPreview () {
    clearWaylinePreviewOnGMap(store, PREFIX)
  }

  function scheduleDraw () {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = undefined
    }
    const run = (attempt: number) => {
      const map = root.$map as any
      if (!map) {
        if (attempt < 40) {
          retryTimer = setTimeout(() => run(attempt + 1), 150)
        }
        return
      }
      drawPreview().catch(() => {})
    }
    run(0)
  }

  async function drawPreview () {
    const map = root.$map as any
    const AMap = root.$aMap as any
    if (!map || !AMap) return

    if (route.name !== ERouterName.FH2_SYNC) {
      clearPreview()
      return
    }

    const preview = store.state.fh2WaylinePreview
    clearPreview()

    if (!preview.projectUuid || !preview.waylineId) {
      return
    }

    const seq = ++reqSeq
    try {
      const blob = await downloadFh2WaylineKmz(preview.projectUuid, preview.waylineId)
      if (seq !== reqSeq) return
      if (!blob || blob.size === 0) {
        return
      }
      const model = await parseWaylineKmzModel(blob)
      if (seq !== reqSeq) return
      if (!hasWaylinePreviewGeometry(model)) {
        return
      }
      const displayName = (model.routeName || preview.waylineName || '航线').trim()
      renderWaylineModelOnGMap(root, model, displayName, PREFIX)
    } catch (e) {
      console.warn('[司空航线地图预览]', e)
    }
  }

  watch(
    () => [
      route.name,
      store.state.fh2WaylinePreview.projectUuid,
      store.state.fh2WaylinePreview.waylineId,
    ] as const,
    () => scheduleDraw(),
    { flush: 'post', immediate: true }
  )

  onBeforeUnmount(() => {
    if (retryTimer) clearTimeout(retryTimer)
    clearPreview()
  })

  return { clearPreview }
}
